import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import {endOfDay, parseISO} from "date-fns";
import AddIcon from "@mui/icons-material/Add";
import {PageHeader} from "../components/ui/PageHeader";
import {colors, fonts} from "../theme/tokens";
import {formatDate} from "../util/format";
import {useAppDispatch, useAppSelector} from "../store/hooks";
import {
    createPromotion,
    deletePromotion,
    fetchPromotions,
    promotionsSelectors,
    updatePromotion,
} from "../store/promotionsSlice";
import type {Promotion, PromotionCreateRequest, PromotionUpdateRequest} from "../types/promotion";
import {PromotionFormDialog} from "../components/promotions/PromotionFormDialog";

type Order = "asc" | "desc";

export function PromotionsPage() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const promotions = useAppSelector(promotionsSelectors.selectAll);
    const status = useAppSelector((s) => s.promotions.status);
    const error = useAppSelector((s) => s.promotions.error);
    const createStatus = useAppSelector((s) => s.promotions.createStatus);
    const updateStatus = useAppSelector((s) => s.promotions.updateStatus);
    const deleteStatus = useAppSelector((s) => s.promotions.deleteStatus);

    const [search, setSearch] = useState("");
    const [orderBy, setOrderBy] = useState<keyof Promotion>("name");
    const [order, setOrder] = useState<Order>("asc");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
    const [snackMessage, setSnackMessage] = useState<string | null>(null);
    const [snackSeverity, setSnackSeverity] = useState<"success" | "error">("success");

    useEffect(() => {
        document.title = t("PROMOTIONS_PAGE_TITLE");
    }, [t]);

    useEffect(() => {
        if (status === "idle") void dispatch(fetchPromotions());
    }, [dispatch, status]);

    const filteredAndSorted = useMemo(() => {
        const q = search.trim().toLowerCase();
        let list = q
                ? promotions.filter(
                        (p) =>
                                p.name.toLowerCase().includes(q) ||
                                p.code.toLowerCase().includes(q),
                )
                : [...promotions];
        list = [...list].sort((a, b) => {
            const aVal = a[orderBy];
            const bVal = b[orderBy];
            if (aVal === bVal) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            const cmp =
                    typeof aVal === "string"
                            ? aVal.localeCompare(String(bVal))
                            : String(aVal).localeCompare(String(bVal));
            return order === "asc" ? cmp : -cmp;
        });
        return list;
    }, [promotions, search, orderBy, order]);

    const handleSort = (column: keyof Promotion) => {
        const isAsc = orderBy === column && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(column);
    };

    const handleCreate = () => {
        setEditingPromotion(null);
        setDialogOpen(true);
    };

    const handleRowClick = (promotion: Promotion) => {
        setEditingPromotion(promotion);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingPromotion(null);
    };

    const handleSubmit = (request: PromotionCreateRequest | PromotionUpdateRequest) => {
        if ("id" in request) {
            dispatch(updatePromotion(request as PromotionUpdateRequest))
                    .unwrap()
                    .then(() => {
                        setSnackMessage(t("PROMOTIONS_UPDATED"));
                        setSnackSeverity("success");
                        handleCloseDialog();
                    })
                    .catch((err) => {
                        setSnackMessage(err?.message ?? t("PROMOTIONS_UPDATE_FAILED"));
                        setSnackSeverity("error");
                    });
        } else {
            dispatch(createPromotion(request as PromotionCreateRequest))
                    .unwrap()
                    .then(() => {
                        setSnackMessage(t("PROMOTIONS_CREATED"));
                        setSnackSeverity("success");
                        handleCloseDialog();
                    })
                    .catch((err) => {
                        setSnackMessage(err?.message ?? t("PROMOTIONS_CREATE_FAILED"));
                        setSnackSeverity("error");
                    });
        }
    };

    const handleDelete = (promotion: Promotion) => {
        dispatch(deletePromotion(promotion.id))
                .unwrap()
                .then(() => {
                    setSnackMessage(t("PROMOTIONS_DELETED"));
                    setSnackSeverity("success");
                    handleCloseDialog();
                })
                .catch((err) => {
                    setSnackMessage(err?.message ?? t("PROMOTIONS_DELETE_FAILED"));
                    setSnackSeverity("error");
                });
    };

    const saving = createStatus === "loading" || updateStatus === "loading";
    const deleting = deleteStatus === "loading";

    const now = new Date();
    const dayOf = (iso: string) => formatDate(iso.slice(0, 10));
    const hasEnded = (promotion: Promotion) => endOfDay(parseISO(promotion.validUntil.slice(0, 10))).getTime() < now.getTime();
    const loading = status === "loading" && promotions.length === 0;
    const sortLabels: Partial<Record<keyof Promotion, string>> = {
        name: t("PROMOTIONS_COLUMN_NAME"),
        code: t("PROMOTIONS_COLUMN_CODE"),
        validFrom: t("PROMOTIONS_COLUMN_VALID_FROM"),
        validUntil: t("PROMOTIONS_COLUMN_VALID_UNTIL"),
        createdAt: t("TENANTS_COLUMN_CREATED"),
    };
    const sortHeader = (column: keyof Promotion) => (
            <TableCell sortDirection={orderBy === column ? order : false}>
                <TableSortLabel active={orderBy === column} direction={orderBy === column ? order : "asc"} onClick={() => handleSort(column)}>
                    {sortLabels[column]}
                </TableSortLabel>
            </TableCell>
    );
    const anyEnded = filteredAndSorted.some(hasEnded);

    return (
            <>
                <PageHeader
                        title={t("NAV_PROMOTIONS")}
                        subtitle={t("PROMOTIONS_SUBTITLE")}
                        actions={
                            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
                                {t("PROMOTIONS_CREATE_BUTTON")}
                            </Button>
                        }
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Box sx={{display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap"}}>
                    <TextField
                            size="small"
                            placeholder={t("PROMOTIONS_SEARCH_PLACEHOLDER")}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{width: 320}}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{fontSize: 18, color: colors.faint}} />
                                            </InputAdornment>
                                    ),
                                },
                                htmlInput: {"aria-label": t("PROMOTIONS_SEARCH_PLACEHOLDER")},
                            }}
                    />
                    <Box sx={{flex: 1}} />
                    <Typography variant="body2" sx={{color: colors.faint}}>
                        {t("TENANTS_SORTED_BY", {column: sortLabels[orderBy] ?? ""})} {order === "asc" ? "↑" : "↓"}
                    </Typography>
                </Box>
                <Paper variant="outlined" sx={{overflow: "hidden"}}>
                    <TableContainer>
                        <Table aria-label={t("PROMOTIONS_TABLE_ARIA")}>
                            <TableHead>
                                <TableRow>
                                    {sortHeader("name")}
                                    {sortHeader("code")}
                                    {sortHeader("validFrom")}
                                    {sortHeader("validUntil")}
                                    {sortHeader("createdAt")}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading &&
                                        Array.from({length: 5}, (_, index) => (
                                                <TableRow key={index}>
                                                    <TableCell colSpan={5}><Skeleton height={24} /></TableCell>
                                                </TableRow>
                                        ))}
                                {!loading && filteredAndSorted.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{py: 4, color: colors.muted}}>
                                                {promotions.length === 0 ? t("PROMOTIONS_EMPTY") : t("PROMOTIONS_NO_MATCH")}
                                            </TableCell>
                                        </TableRow>
                                )}
                                {filteredAndSorted.map((promotion) => {
                                    const ended = hasEnded(promotion);
                                    return (
                                            <TableRow
                                                    key={promotion.id}
                                                    hover
                                                    tabIndex={0}
                                                    onClick={() => handleRowClick(promotion)}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter") handleRowClick(promotion);
                                                    }}
                                                    sx={{cursor: "pointer", "& td": ended ? {color: colors.faint} : undefined}}
                                            >
                                                <TableCell sx={{fontWeight: 500, color: ended ? undefined : colors.ink}}>
                                                    {promotion.name}
                                                    {ended && <Box component="span" sx={{position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)"}}> ({t("PROMOTIONS_ENDED")})</Box>}
                                                </TableCell>
                                                <TableCell sx={{fontFamily: fonts.mono, fontSize: "12px"}}>{promotion.code}</TableCell>
                                                <TableCell>{dayOf(promotion.validFrom)}</TableCell>
                                                <TableCell>{dayOf(promotion.validUntil)}</TableCell>
                                                <TableCell sx={{color: colors.faint}}>{formatDate(promotion.createdAt)}</TableCell>
                                            </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
                {anyEnded && <Typography variant="body2" sx={{color: colors.faint}}>{t("PROMOTIONS_ENDED_HINT")}</Typography>}

                <PromotionFormDialog
                        open={dialogOpen}
                        promotion={editingPromotion}
                        onClose={handleCloseDialog}
                        onSubmit={handleSubmit}
                        onDelete={editingPromotion ? handleDelete : undefined}
                        saving={saving}
                        deleting={deleting}
                />

                <Snackbar open={snackMessage != null} autoHideDuration={6000} onClose={() => setSnackMessage(null)} anchorOrigin={{vertical: "bottom", horizontal: "center"}}>
                    <Alert severity={snackSeverity} variant="filled" onClose={() => setSnackMessage(null)}>
                        {snackMessage}
                    </Alert>
                </Snackbar>
            </>
    );
}
